import { getThemeLayoutClass } from '../../theme/crystalLayout';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import { Page } from '../../types';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { ShoppingCart, Menu, X } from 'lucide-react';

const PrismaNavbar: React.FC = () => {
    const context = useContext(AppContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);

    if (!context) return null;
    const { setPage, cart, footerInfo, setIsCartOpen, page: currentPage, language, setLanguage, t } = context;

    const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

    const navItems = [
        { label: t('nav.home', 'Accueil'), page: Page.Home, href: '/' },
        { label: t('nav.repairs', 'Réparations'), page: Page.Repairs, href: '/reparation' },
        { label: t('nav.buy', 'Acheter'), page: Page.BuyPhone, href: '/acheter' },
        { label: t('nav.sell', 'Revendre'), page: Page.SellPhone, href: '/rachat' },
        { label: t('nav.accessories', 'Accessoires'), page: Page.Accessories, href: '/accessoires' },
        { label: t('nav.blog', 'Blog'), page: Page.Blog, href: '/blog' },
        { label: t('nav.devices', 'Appareils'), page: Page.Devices, href: '/appareils' },
        { label: t('nav.find_model', 'Aide'), page: Page.FindMyModel, href: '/aide' },
        { label: t('nav.diagnostic', 'Diagnostic'), page: Page.ImeiVerification, href: '/imei' },
        { label: t('nav.contact', 'Contact'), page: Page.Contact, href: '/contact' },
    ];

    const handleNavClick = (targetPage: Page) => {
        setPage(targetPage);
        setIsMobileMenuOpen(false);
        setIsLangOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            {/* Desktop Navbar */}
            <nav className={getThemeLayoutClass(context?.activeThemeConfig, 'navigationDesktop')}>
                <div className={getThemeLayoutClass(context?.activeThemeConfig, 'navigationSurface')}>
                    <a 
                        href="/"
                        className="flex items-center gap-2 cursor-pointer group lg:ml-1.5 lg:pr-1 xl:ml-3.5 xl:pr-2" 
                        onClick={(e) => { e.preventDefault(); handleNavClick(Page.Home); }}
                    >
                        {footerInfo.logo_url ? (
                            <img src={footerInfo.logo_url} alt={footerInfo.siteName || footerInfo.nom_du_magasin} className="h-4 xl:h-5 w-auto object-contain transition-transform group-hover:scale-105" width="80" height="20" />
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-[14px] xl:text-[17px] font-black text-white tracking-[0.05em] uppercase select-none">
                                    {(footerInfo.siteName || footerInfo.nom_du_magasin || 'MOBILBO').toUpperCase()}
                                </span>
                            </div>
                        )}
                    </a>

                    <div className="flex items-center gap-0.5">
                        {navItems.map((item) => (
                            <a
                                key={`prisma-nav-${item.page}`}
                                href={item.href}
                                onClick={(e) => { e.preventDefault(); handleNavClick(item.page); }}
                                className={`${getThemeLayoutClass(context?.activeThemeConfig, 'navigationLink')}${
                                    currentPage === item.page 
                                    ? 'bg-[#201f27] text-white border border-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.4)]' 
                                    : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
                                }`}
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>

                    {/* Right utilities (Cart + Language Switcher) */}
                    <div className="flex items-center lg:gap-1.5 lg:ml-0.5 lg:pr-0.5 xl:gap-2 xl:ml-1 xl:pr-1 font-sans">
                        {/* Language Selector Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsLangOpen(!isLangOpen)}
                                aria-label={`Changer de langue (${language.toUpperCase()})`}
                                aria-expanded={isLangOpen}
                                className="lg:w-9 lg:h-9 xl:w-10 xl:h-10 flex items-center justify-center rounded-full bg-[#1e1d24] hover:bg-white/10 text-white border border-white/10 transition-all active:scale-95 text-[9px] lg:text-[10px] font-black tracking-widest uppercase gap-0.5"
                                title="Changer la langue / Sprache wechseln / Change language"
                            >
                                <span>{language}</span>
                                <span className="text-[7px] text-white/45">▼</span>
                            </button>
                            <AnimatePresence>
                                {isLangOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        className="absolute top-12 right-0 bg-[#16151a] border border-white/10 rounded-2xl p-1.5 min-w-[120px] shadow-[0_15px_40px_rgba(0,0,0,0.7)] flex flex-col gap-1 z-[9999]"
                                    >
                                        {[
                                            { code: 'fr', label: 'FR', name: 'Français', flag: '🇨🇭' },
                                            { code: 'de', label: 'DE', name: 'Deutsch', flag: '🇨🇭' },
                                            { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' }
                                        ].map((lang) => (
                                            <button
                                                key={lang.code}
                                                onClick={() => {
                                                    setLanguage(lang.code as 'fr' | 'de' | 'en');
                                                    setIsLangOpen(false);
                                                }}
                                                className={`w-full text-left py-2 px-3 rounded-xl text-[10px] uppercase tracking-widest font-black flex items-center justify-between transition-colors ${
                                                    language === lang.code 
                                                    ? 'bg-blue-600/25 text-blue-400 border border-blue-500/25' 
                                                    : 'text-white/70 hover:text-white hover:bg-white/5'
                                                }`}
                                            >
                                                <span>{lang.name}</span>
                                                <span className="text-sm select-none">{lang.flag}</span>
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Cart Button */}
                        <button
                            onClick={() => setIsCartOpen(true)}
                            aria-label="Ouvrir le panier"
                            className="relative lg:w-9 lg:h-9 xl:w-10 xl:h-10 flex items-center justify-center rounded-full bg-[#1e1d24] hover:bg-white/10 text-white border border-white/10 transition-all active:scale-95 group"
                        >
                            <ShoppingCart className="w-3.5 h-3.5 xl:w-4 xl:h-4 group-hover:scale-105 transition-transform" />
                            {cartCount > 0 && (
                                <div className="absolute -top-1 -right-1 w-4.5 h-4.5 xl:w-5 xl:h-5 bg-[#3b82f6] text-white text-[8px] xl:text-[9px] font-black flex items-center justify-center rounded-full border-2 border-[#111015]">
                                    {cartCount}
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Navbar */}
            <nav className={getThemeLayoutClass(context?.activeThemeConfig, 'navigationMobile')}>
                <a 
                    href="/" 
                    className="flex items-center gap-2 cursor-pointer" 
                    onClick={(e) => { e.preventDefault(); handleNavClick(Page.Home); }}
                >
                    {footerInfo.logo_url ? (
                        <img src={footerInfo.logo_url} alt={footerInfo.siteName || footerInfo.nom_du_magasin} className="h-6 w-auto object-contain" width="96" height="24" />
                    ) : (
                        <span className="text-sm font-black text-white tracking-widest uppercase ml-1.5">
                            {(footerInfo.siteName || footerInfo.nom_du_magasin || 'MOBILBO').toUpperCase()}
                        </span>
                    )}
                </a>

                <div className="flex items-center gap-2.5 font-sans">
                    {/* Mobile Quick Cycle Language Toggle Button */}
                    <button
                        onClick={() => {
                            const cycle: Record<'fr' | 'de' | 'en', 'fr' | 'de' | 'en'> = { fr: 'de', de: 'en', en: 'fr' };
                            setLanguage(cycle[language]);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white border border-white/10 text-[9px] font-black uppercase tracking-wider"
                        title="Changer de langue / Sprache wechseln / Switch language"
                        aria-label={`Changer de langue (${language.toUpperCase()})`}
                    >
                        {language}
                    </button>

                    {/* Shopping Cart */}
                    <button
                        onClick={() => setIsCartOpen(true)}
                        aria-label="Ouvrir le panier"
                        className="relative w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white border border-white/10 transition-all active:scale-95"
                    >
                        <ShoppingCart className="w-3.5 h-3.5" />
                        {cartCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#3b82f6] text-white text-[8px] font-black flex items-center justify-center rounded-full">
                                {cartCount}
                            </div>
                        )}
                    </button>

                    {/* Menu Toggle */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                        aria-expanded={isMobileMenuOpen}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white border border-white/10 transition-all active:scale-95"
                    >
                        {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                        animate={{ opacity: 1, backdropFilter: 'blur(24px)' }}
                        exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                        className="fixed inset-0 z-[998] bg-[#050510]/95 lg:hidden pt-24 px-6 overflow-y-auto pb-12 font-sans"
                    >
                        {/* Mobile Language Switcher (Inside Overlay) */}
                        <div className="mb-6 p-4 rounded-3xl bg-[#111015] border border-white/5">
                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-500 block mb-2 px-1">
                                {language === 'fr' ? 'SÉLECTIONNER LA LANGUE' : language === 'de' ? 'SPRACHE WÄHLEN' : 'SELECT LANGUAGE'}
                            </span>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { code: 'fr', label: 'Français 🇨🇭' },
                                    { code: 'de', label: 'Deutsch 🇨🇭' },
                                    { code: 'en', label: 'English 🇬🇧' }
                                ].map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => setLanguage(lang.code as 'fr' | 'de' | 'en')}
                                        className={`py-2 px-2.5 rounded-xl text-[9px] font-black tracking-wider uppercase text-center transition-all ${
                                            language === lang.code
                                            ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20'
                                            : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                                        }`}
                                    >
                                        {lang.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            {navItems.map((item) => (
                                <motion.a
                                    key={`prisma-nav-mobile-${item.page}`}
                                    href={item.href}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ delay: 0.05 }}
                                    onClick={(e) => { e.preventDefault(); handleNavClick(item.page); }}
                                    className={`w-full text-left py-4 px-6 rounded-2xl text-lg font-black uppercase tracking-widest block ${
                                        currentPage === item.page
                                        ? 'bg-gradient-to-r from-indigo-500/20 to-transparent text-indigo-400 border border-indigo-500/20'
                                        : 'text-white/70 hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    {item.label}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default PrismaNavbar;
